/**
 * Sync Service - Offline-first synchronization with Azure Table Storage
 *
 * Key features:
 * - 304 Not-Modified checks using blob-stored manifests
 * - Push local changes first, then pull server changes
 * - Conflict detection with last-write-wins resolution
 * - Background sync scheduling
 */

import { dbManager, getDb, type SyncStatus } from "../../db/user-database";
import type {
  SyncManifest,
  SyncCheckResult,
  PushOperation,
  PushResult,
  PullResult,
  SyncResult,
  SyncState,
  MonitoredUserDataResponse,
} from "./types";
import { INITIAL_SYNC_STATE } from "./types";
import { apiBase } from "../api-config";

// API base URL (dynamic for dev vs prod)
const API_BASE = apiBase;

// Sync intervals
const PERIODIC_CHECK_INTERVAL = 5 * 60 * 1000; // 5 minutes
const DEBOUNCE_DELAY = 2000; // 2 seconds after local write

// Table names for sync
const SYNC_TABLES = [
  "entries",
  "periods",
  "settings",
  "cgmReadings",
  "dayNotes",
] as const;
type SyncTable = (typeof SYNC_TABLES)[number];

// Sync state listener
type SyncStateListener = (state: SyncState) => void;

class SyncService {
  private _state: SyncState = INITIAL_SYNC_STATE;
  private _listeners: Set<SyncStateListener> = new Set();
  private _syncInProgress = false;
  private _syncTimer: number | null = null;
  private _periodicTimer: number | null = null;
  private _cachedEtag: string | null = null;
  private _isOnline = navigator.onLine;

  /**
   * Current sync state
   */
  get state(): SyncState {
    return this._state;
  }

  /**
   * Subscribe to sync state changes
   */
  subscribe(listener: SyncStateListener): () => void {
    this._listeners.add(listener);
    listener(this._state);
    return () => {
      this._listeners.delete(listener);
    };
  }

  /**
   * Update state and notify listeners
   */
  private setState(partial: Partial<SyncState>): void {
    this._state = { ...this._state, ...partial };
    this._listeners.forEach((listener) => listener(this._state));
  }

  /**
   * Initialize sync service
   */
  async initialize(): Promise<void> {
    // Set up online/offline listeners
    window.addEventListener("online", () => this.onOnline());
    window.addEventListener("offline", () => this.onOffline());
    this._isOnline = navigator.onLine;

    if (!this._isOnline) {
      this.setState({ status: "offline" });
      return;
    }

    // Initial sync check
    await this.checkAndSync();

    // Start periodic checks
    this.startPeriodicCheck();
  }

  /**
   * Start periodic sync checks
   */
  private startPeriodicCheck(): void {
    if (this._periodicTimer) {
      clearInterval(this._periodicTimer);
    }

    this._periodicTimer = window.setInterval(() => {
      if (this._isOnline && !this._syncInProgress) {
        this.checkAndSync();
      }
    }, PERIODIC_CHECK_INTERVAL);
  }

  /**
   * Stop periodic checks
   */
  private stopPeriodicCheck(): void {
    if (this._periodicTimer) {
      clearInterval(this._periodicTimer);
      this._periodicTimer = null;
    }
  }

  /**
   * Handle coming online
   */
  private async onOnline(): Promise<void> {
    this._isOnline = true;
    this.setState({ status: "idle" });

    // Immediate sync when coming online
    await this.checkAndSync();
    this.startPeriodicCheck();
  }

  /**
   * Handle going offline
   */
  private onOffline(): void {
    this._isOnline = false;
    this.setState({ status: "offline" });
    this.stopPeriodicCheck();
  }

  /**
   * Quick check for updates using 304 Not-Modified
   */
  async checkForUpdates(): Promise<SyncCheckResult> {
    if (!this._isOnline || !dbManager.isInitialized) {
      return { hasChanges: false, offline: !this._isOnline };
    }

    try {
      this.setState({ status: "checking" });

      const headers: HeadersInit = {
        Accept: "application/json",
      };

      // Add ETag for 304 check
      if (this._cachedEtag) {
        headers["If-None-Match"] = this._cachedEtag;
      }

      // Add last modified from sync metadata
      const syncMeta = await getDb().syncMetadata.get("state");
      if (syncMeta?.lastSyncTimestamp) {
        headers["If-Modified-Since"] = syncMeta.lastSyncTimestamp;
      }

      const response = await fetch(`${API_BASE}/api/sync/manifest`, {
        method: "GET",
        credentials: "include",
        headers,
      });

      // 304 - No changes
      if (response.status === 304) {
        this.setState({ status: "idle" });
        return { hasChanges: false };
      }

      // 401 - Not authenticated
      if (response.status === 401) {
        this.setState({ status: "idle" });
        return { hasChanges: false };
      }

      // Error response
      if (!response.ok) {
        throw new Error(`Server error: ${response.status}`);
      }

      // Parse manifest
      const manifest: SyncManifest = await response.json();
      const etag = response.headers.get("ETag") || undefined;

      if (etag) {
        this._cachedEtag = etag;
      }

      return {
        hasChanges: true,
        manifest,
        etag,
      };
    } catch (error) {
      console.error("Sync check failed:", error);
      const errorMessage =
        error instanceof Error ? error.message : "Sync check failed";
      this.setState({
        status: "error",
        error: errorMessage,
      });
      return { hasChanges: false, error: errorMessage };
    }
  }

  /**
   * Push local changes to server
   * Batches operations for better performance with large datasets
   */
  async pushChanges(): Promise<PushResult> {
    const db = getDb();
    const operations: PushOperation[] = [];

    // Collect all pending changes
    for (const table of SYNC_TABLES) {
      const pending = await (db[table] as any)
        .where("_syncStatus")
        .equals("pending")
        .toArray();

      for (const item of pending) {
        const isDeleted = !!item._deletedAt;
        operations.push({
          type: isDeleted
            ? "delete"
            : item._serverVersion
              ? "update"
              : "create",
          table,
          id: item.id,
          data: item,
          localVersion: item._localVersion || Date.now(),
          serverVersion: item._serverVersion,
        });
      }
    }

    if (operations.length === 0) {
      return { succeeded: [], conflicts: [], errors: [] };
    }

    // Batch size - balance between HTTP overhead and request size
    const BATCH_SIZE = 500;
    const allSucceeded: PushResult["succeeded"] = [];
    const allConflicts: PushResult["conflicts"] = [];
    const allErrors: PushResult["errors"] = [];

    // Process in batches
    for (let i = 0; i < operations.length; i += BATCH_SIZE) {
      const batch = operations.slice(i, i + BATCH_SIZE);
      const batchNum = Math.floor(i / BATCH_SIZE) + 1;
      const totalBatches = Math.ceil(operations.length / BATCH_SIZE);

      console.log(
        `Syncing batch ${batchNum}/${totalBatches} (${batch.length} items)`,
      );

      try {
        const response = await fetch(`${API_BASE}/api/sync/push`, {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({ operations: batch }),
        });

        if (!response.ok) {
          throw new Error(`Push failed: ${response.status}`);
        }

        const result: PushResult = await response.json();

        allSucceeded.push(...result.succeeded);
        allConflicts.push(...result.conflicts);
        allErrors.push(...result.errors);

        // Update local records with server versions for this batch
        for (const success of result.succeeded) {
          const table = db[success.table as SyncTable] as any;
          await table.update(success.id, {
            _syncStatus: "synced" as SyncStatus,
            _serverVersion: success.serverVersion,
            _localVersion: undefined,
          });
        }

        // Handle conflicts for this batch
        for (const conflict of result.conflicts) {
          const table = db[conflict.table as SyncTable] as any;
          const serverData = conflict.serverData as Record<string, unknown>;
          await table.update(conflict.id, {
            ...serverData,
            _syncStatus: "conflict" as SyncStatus,
            _serverVersion: conflict.serverVersion,
            _conflictData: conflict.localData,
            _conflictAt: new Date().toISOString(),
          });
        }
      } catch (error) {
        console.error(`Batch ${batchNum} failed:`, error);
        // Mark remaining items in this batch as errors
        for (const op of batch) {
          allErrors.push({
            table: op.table,
            id: op.id,
            error: error instanceof Error ? error.message : "Batch failed",
          });
        }
        // Continue with next batch instead of failing entirely
      }
    }

    // Update pending count
    await this.updatePendingCount();

    console.log(
      `Sync complete: ${allSucceeded.length} succeeded, ${allConflicts.length} conflicts, ${allErrors.length} errors`,
    );

    return {
      succeeded: allSucceeded,
      conflicts: allConflicts,
      errors: allErrors,
    };
  }

  /**
   * Pull changes from server
   */
  async pullChanges(manifest: SyncManifest): Promise<void> {
    const db = getDb();
    const syncMeta = await db.syncMetadata.get("state");

    // Check which tables have changes by comparing per-table versions
    // Only pull tables where server version > local version
    const tablesToPull: SyncTable[] = [];

    for (const table of SYNC_TABLES) {
      const serverVersion =
        manifest.tableVersions?.[
          table as keyof typeof manifest.tableVersions
        ] ?? 0;
      const localVersion = syncMeta?.tableVersions?.[table] ?? 0;

      // If server version is higher, we need to pull
      if (serverVersion > localVersion) {
        tablesToPull.push(table);
      }
    }

    if (tablesToPull.length === 0) {
      return;
    }

    // Pull each changed table
    for (const table of tablesToPull) {
      try {
        const sinceVersion = syncMeta?.tableVersions?.[table] || 0;

        // For CGM readings, limit to last 90 days to avoid pulling entire history
        const pullBody: { sinceVersion: number; sinceDate?: string } = {
          sinceVersion,
        };
        if (table === "cgmReadings") {
          const ninetyDaysAgo = new Date();
          ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);
          pullBody.sinceDate = ninetyDaysAgo.toISOString();
        }

        const response = await fetch(`${API_BASE}/api/sync/pull/${table}`, {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify(pullBody),
        });

        if (!response.ok) {
          console.error(`Pull failed for ${table}: ${response.status}`);
          continue;
        }

        const result: PullResult = await response.json();

        // Apply changes
        const tableRef = db[table] as any;

        for (const change of result.changes) {
          if (change.isDeleted) {
            // Remove deleted items (unless we have local changes)
            const local = await tableRef.get(change.id);
            if (!local || local._syncStatus !== "pending") {
              await tableRef.delete(change.id);
            }
          } else {
            // Upsert with server data
            const local = await tableRef.get(change.id);

            // Skip if we have pending local changes
            if (local && local._syncStatus === "pending") {
              continue;
            }

            const changeData = change.data as Record<string, unknown>;
            await tableRef.put({
              ...changeData,
              id: change.id,
              _syncStatus: "synced" as SyncStatus,
              _serverVersion: change.version,
            });
          }
        }

        // Update table version
        const currentMeta = (await db.syncMetadata.get("state")) || {
          key: "state" as const,
          localVersion: "",
          tableVersions: {},
        };

        await db.syncMetadata.put({
          ...currentMeta,
          tableVersions: {
            ...currentMeta.tableVersions,
            [table]: result.currentVersion,
          },
        });
      } catch (error) {
        console.error(`Error pulling ${table}:`, error);
      }
    }

    // Update sync metadata with server versions
    const finalMeta = await db.syncMetadata.get("state");
    await db.syncMetadata.put({
      key: "state",
      lastSyncTimestamp: new Date().toISOString(),
      localVersion: manifest.etag || String(manifest.version),
      serverChecksum: JSON.stringify(manifest.tableVersions),
      tableVersions: finalMeta?.tableVersions || {},
    });
  }

  /**
   * Full sync operation: check → push → pull
   */
  async sync(): Promise<SyncResult> {
    if (this._syncInProgress) {
      return { success: false, skipped: true };
    }

    if (!this._isOnline || !dbManager.isInitialized) {
      return { success: false, error: "Offline or not initialized" };
    }

    this._syncInProgress = true;
    this.setState({ status: "syncing" });

    try {
      // 1. Check for server changes
      const checkResult = await this.checkForUpdates();

      // 2. Push local changes first
      const pushResult = await this.pushChanges();
      const pushedCount = pushResult.succeeded.length;
      const conflictCount = pushResult.conflicts.length;

      // 3. Pull server changes if manifest indicates changes
      let pulledCount = 0;
      if (checkResult.hasChanges && checkResult.manifest) {
        await this.pullChanges(checkResult.manifest);
        pulledCount = Object.values(checkResult.manifest.counts).reduce(
          (a, b) => a + b,
          0,
        );
      }

      // Update state
      await this.updatePendingCount();
      this.setState({
        status: "idle",
        lastSyncTime: new Date(),
        conflicts: conflictCount,
        error: undefined,
      });

      return {
        success: true,
        pushed: pushedCount,
        pulled: pulledCount,
        conflicts: conflictCount,
      };
    } catch (error) {
      console.error("Sync failed:", error);
      this.setState({
        status: "error",
        error: error instanceof Error ? error.message : "Sync failed",
      });
      return {
        success: false,
        error: error instanceof Error ? error.message : "Sync failed",
      };
    } finally {
      this._syncInProgress = false;
    }
  }

  /**
   * Convenience method: check and sync if needed
   */
  async checkAndSync(): Promise<SyncResult> {
    const checkResult = await this.checkForUpdates();

    // If check failed with error, return failure
    if (checkResult.error) {
      return { success: false, error: checkResult.error };
    }

    // Also sync if we have pending local changes
    const pendingCount = await this.getPendingCount();

    if (checkResult.hasChanges || pendingCount > 0) {
      return this.sync();
    }

    return { success: true, pushed: 0, pulled: 0 };
  }

  /**
   * Schedule sync soon (debounced) - call after local writes
   */
  scheduleSyncSoon(): void {
    if (this._syncTimer) {
      clearTimeout(this._syncTimer);
    }

    this._syncTimer = window.setTimeout(() => {
      this._syncTimer = null;
      if (this._isOnline && !this._syncInProgress) {
        this.sync();
      }
    }, DEBOUNCE_DELAY);
  }

  /**
   * Get count of pending changes
   */
  private async getPendingCount(): Promise<number> {
    if (!dbManager.isInitialized) return 0;

    const db = getDb();
    let total = 0;

    for (const table of SYNC_TABLES) {
      const count = await (db[table] as any)
        .where("_syncStatus")
        .equals("pending")
        .count();
      total += count;
    }

    return total;
  }

  /**
   * Update pending count in state
   */
  private async updatePendingCount(): Promise<void> {
    const pendingCount = await this.getPendingCount();

    // Also count conflicts
    let conflictCount = 0;
    if (dbManager.isInitialized) {
      const db = getDb();
      for (const table of SYNC_TABLES) {
        const count = await (db[table] as any)
          .where("_syncStatus")
          .equals("conflict")
          .count();
        conflictCount += count;
      }
    }

    this.setState({
      pendingChanges: pendingCount,
      conflicts: conflictCount,
    });
  }

  /**
   * Cleanup on logout
   */
  cleanup(): void {
    this.stopPeriodicCheck();
    if (this._syncTimer) {
      clearTimeout(this._syncTimer);
      this._syncTimer = null;
    }
    this._cachedEtag = null;
    this.setState(INITIAL_SYNC_STATE);
  }

  // ==========================================
  // Monitored User Data Access (Read-Only)
  // ==========================================

  /**
   * Get manifest for a monitored user (for monitors/clinicians)
   * Returns data directly - does not sync to local DB
   */
  async getMonitoredManifest(
    diabeticUserId: string,
  ): Promise<SyncManifest | null> {
    try {
      const response = await fetch(
        `${API_BASE}/api/sync/monitored/${diabeticUserId}/manifest`,
        {
          method: "GET",
          credentials: "include",
          headers: {
            Accept: "application/json",
          },
        },
      );

      if (response.status === 403 || response.status === 404) {
        return null;
      }

      if (!response.ok) {
        throw new Error(`Server error: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error("Failed to get monitored manifest:", error);
      return null;
    }
  }

  /**
   * Pull data for a specific table from a monitored user
   * Returns data directly - does not sync to local DB
   */
  async pullMonitoredData(
    diabeticUserId: string,
    table: SyncTable,
    sinceVersion = 0,
  ): Promise<PullResult | null> {
    try {
      // For CGM readings, limit to last 90 days
      const pullBody: { sinceVersion: number; sinceDate?: string } = {
        sinceVersion,
      };
      if (table === "cgmReadings") {
        const ninetyDaysAgo = new Date();
        ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);
        pullBody.sinceDate = ninetyDaysAgo.toISOString();
      }

      const response = await fetch(
        `${API_BASE}/api/sync/monitored/${diabeticUserId}/pull/${table}`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify(pullBody),
        },
      );

      if (response.status === 403 || response.status === 404) {
        return null;
      }

      if (!response.ok) {
        throw new Error(`Server error: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error(`Failed to pull monitored data for ${table}:`, error);
      return null;
    }
  }

  /**
   * Fetch all data for a monitored user (for viewing dashboard)
   * Uses the single /data endpoint which returns all tables in one request
   * CGM readings are server-filtered to last 90 days
   */
  async fetchMonitoredUserData(diabeticUserId: string): Promise<{
    entries: unknown[];
    periods: unknown[];
    settings: unknown[];
    cgmReadings: unknown[];
    dayNotes: unknown[];
  } | null> {
    try {
      const response = await fetch(
        `${API_BASE}/api/sync/monitored/${diabeticUserId}/data`,
        {
          method: "GET",
          credentials: "include",
          headers: {
            Accept: "application/json",
          },
        },
      );

      if (response.status === 403 || response.status === 404) {
        return null;
      }

      if (!response.ok) {
        throw new Error(`Server error: ${response.status}`);
      }

      const data: MonitoredUserDataResponse = await response.json();

      return {
        entries: data.entries,
        periods: data.periods,
        settings: data.settings,
        cgmReadings: data.cgmReadings,
        dayNotes: data.dayNotes || [],
      };
    } catch (error) {
      console.error("Failed to fetch monitored user data:", error);
      return null;
    }
  }

  /**
   * Add a clinic note to a monitored user's data.
   * This allows clinicians to add notes that will appear in the patient's history view.
   */
  async addMonitoredUserNote(
    diabeticUserId: string,
    date: string,
    content: string,
  ): Promise<{ noteId: string; createdAt: string } | null> {
    try {
      const response = await fetch(
        `${API_BASE}/api/sync/monitored/${diabeticUserId}/note`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ date, content }),
        },
      );

      if (response.status === 403 || response.status === 404) {
        console.error("Not authorized to add notes to this user");
        return null;
      }

      if (!response.ok) {
        throw new Error(`Server error: ${response.status}`);
      }

      const result = await response.json();
      return {
        noteId: result.noteId,
        createdAt: result.createdAt,
      };
    } catch (error) {
      console.error("Failed to add note to monitored user:", error);
      return null;
    }
  }

  /**
   * Delete all user data from both local storage and server.
   * This is a destructive operation that cannot be undone.
   */
  async deleteAllData(): Promise<{ success: boolean; error?: string }> {
    try {
      this.setState({ status: "syncing" });

      // First, delete from server if online
      if (this._isOnline) {
        try {
          const response = await fetch(`${API_BASE}/api/sync/data`, {
            method: "DELETE",
            credentials: "include",
          });

          if (!response.ok && response.status !== 404) {
            // 404 is ok - means no data to delete
            console.warn("Server deletion warning:", response.status);
          }
        } catch (serverError) {
          // Log but continue with local deletion
          console.warn(
            "Server deletion failed, continuing with local:",
            serverError,
          );
        }
      }

      // Delete all local databases
      const db = await getDb();
      if (db) {
        // Clear all tables
        await db.entries.clear();
        await db.periods.clear();
        await db.settings.clear();
        await db.cgmReadings.clear();
        await db.syncMetadata.clear();
      }

      // Reset cached etag
      this._cachedEtag = null;

      this.setState({
        status: this._isOnline ? "idle" : "offline",
        lastSyncTime: undefined,
        pendingChanges: 0,
      });

      return { success: true };
    } catch (error) {
      console.error("Failed to delete all data:", error);
      this.setState({ status: "error" });
      return {
        success: false,
        error: error instanceof Error ? error.message : "Unknown error",
      };
    }
  }

  /**
   * Force push all local data to server
   * Marks all items as pending first, then pushes
   */
  async forcePushAll(): Promise<SyncResult> {
    if (this._syncInProgress) {
      return { success: false, error: "Sync already in progress" };
    }

    if (!this._isOnline) {
      return { success: false, error: "Offline" };
    }

    this._syncInProgress = true;
    this.setState({ status: "syncing" });

    try {
      const db = getDb();
      let markedCount = 0;

      // Mark all items in sync tables as pending
      for (const table of SYNC_TABLES) {
        const items = await (db[table] as any).toArray();
        for (const item of items) {
          if (!item._deletedAt) {
            await (db[table] as any).update(item.id, {
              _syncStatus: "pending" as SyncStatus,
              _localVersion: Date.now(),
            });
            markedCount++;
          }
        }
      }

      console.log(`Marked ${markedCount} items for force push`);

      // Now push all pending changes
      const pushResult = await this.pushChanges();
      const pushedCount = pushResult.succeeded.length;
      const errorCount = pushResult.errors.length;

      this.setState({
        status: "idle",
        lastSyncTime: new Date(),
        error: undefined,
      });

      await this.updatePendingCount();

      return {
        success: errorCount === 0,
        pushed: pushedCount,
        error:
          errorCount > 0 ? `${errorCount} items failed to push` : undefined,
      };
    } catch (error) {
      console.error("Force push failed:", error);
      this.setState({
        status: "error",
        error: error instanceof Error ? error.message : "Force push failed",
      });
      return {
        success: false,
        error: error instanceof Error ? error.message : "Force push failed",
      };
    } finally {
      this._syncInProgress = false;
    }
  }
}

// Singleton instance
export const syncService = new SyncService();

// Re-export types
export * from "./types";
